/**
 * Client-side global error reporter. Catches window errors and unhandled
 * promise rejections and POSTs them to /api/log/error so they land in
 * Vercel logs. Uses sendBeacon with fetch fallback so reports survive tab
 * kills (Safari "This page couldn't load" was eating our errors).
 *
 * Wire it up by importing the module once in layout.tsx — the install()
 * call at module load is idempotent.
 */

import { redactSensitiveLogValue } from "@/lib/redactSecrets";

type ErrorPayload = {
  kind: "error" | "unhandledrejection";
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  column?: number;
  url: string;
  userAgent: string;
  ts: number;
  workspaceId?: string | null;
  workspaceSlug?: string | null;
};

const recentHashes = new Map<string, number>();
const DEDUP_WINDOW_MS = 2000;

function hash(s: string): string {
  // Tiny FNV-1a-ish hash; we only need dedup within a 2s window.
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h.toString(16);
}

function send(payload: ErrorPayload) {
  const safePayload = redactSensitiveLogValue(payload) as ErrorPayload;
  const key = hash(safePayload.message + "|" + (safePayload.stack ?? ""));
  const now = Date.now();
  const last = recentHashes.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) return;
  recentHashes.set(key, now);

  const body = JSON.stringify(safePayload);
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        "/api/log/error",
        new Blob([body], { type: "application/json" })
      );
      if (ok) return;
    }
  } catch {
    /* fall through to fetch */
  }
  try {
    fetch("/api/log/error", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* swallow — we don't want the reporter itself to throw */
  }
}

function activeWorkspaceContext(): {
  workspaceId: string | null;
  workspaceSlug: string | null;
} {
  if (typeof window === "undefined") return { workspaceId: null, workspaceSlug: null };
  // Avoid a store import cycle: read the URL slug + localStorage directly.
  const slug = window.location.pathname.split("/").filter(Boolean)[0] ?? null;
  const id = window.localStorage.getItem("pauta:activeWorkspaceId");
  return { workspaceId: id, workspaceSlug: slug };
}

function basePayload(): Pick<ErrorPayload, "url" | "userAgent" | "ts" | "workspaceId" | "workspaceSlug"> {
  const ctx = activeWorkspaceContext();
  return {
    url: typeof window !== "undefined" ? window.location.href : "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    ts: Date.now(),
    workspaceId: ctx.workspaceId,
    workspaceSlug: ctx.workspaceSlug,
  };
}

let installed = false;

export function install() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (e) => {
    send({
      kind: "error",
      message: e.message ?? String(e.error ?? "unknown"),
      stack: e.error?.stack,
      source: e.filename,
      line: e.lineno,
      column: e.colno,
      ...basePayload(),
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    send({
      kind: "unhandledrejection",
      message:
        (reason && (reason.message || String(reason))) || "unhandled promise rejection",
      stack: reason?.stack,
      ...basePayload(),
    });
  });
}

export function reportBoundary(info: {
  message: string;
  stack?: string;
  componentStack?: string;
}) {
  send({
    kind: "error",
    message: `[boundary] ${info.message}`,
    stack: [info.stack, info.componentStack].filter(Boolean).join("\n---\n"),
    ...basePayload(),
  });
}

// Auto-install on module load in the browser.
install();
